import { Box, Checkbox, Link, Typography, useTheme } from '@mui/material';
import React, { useCallback } from 'react';

import { OptionGroup } from './FilterBar.props';
import { OPTION_FONT_SIZE } from './filterBarStyles';

/** The option list rows rendered inside `MultiSelectDropdown`'s popper.
 *  Extracted from that component to keep it under the project's 250-line
 *  file budget, the same split `FilterBarBuiltInFields` makes for `FilterBar`. */

export interface OptionRowProps {
  label: string;
  checked: boolean;
  onClick: () => void;
  accentColor: string;
  hoverBg: string;
}

export const OptionRow: React.FC<OptionRowProps> = ({
  label,
  checked,
  onClick,
  accentColor,
  hoverBg,
}) => {
  const theme = useTheme();
  // The row is a div, so it needs its own key handling — without this the
  // options could only ever be ticked with a pointer. Space is the native
  // toggle key for a checkbox and would otherwise scroll the option list.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        // Enter would also bubble up to FilterBar's Enter-to-search handler.
        e.stopPropagation();
        onClick();
      }
    },
    [onClick]
  );

  return (
    <Box
      role="option"
      aria-selected={checked}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 0.5,
        py: 0.125,
        cursor: 'pointer',
        // Square, not rounded: consecutive ticked rows have to read as one
        // block, and a 4px radius left a sliver of background showing through
        // at every join.
        borderRadius: 0,
        // A ticked row takes a slate fill, not a colour: the tick and the
        // label weight already say it is selected, so the row only has to
        // separate itself from its neighbours.
        ...(checked && { bgcolor: theme.palette.v4.rowSelected }),
        '&:hover': { bgcolor: checked ? theme.palette.v4.rowSelectedHover : hoverBg },
        '&:focus-visible': { outline: `2px solid ${accentColor}`, outlineOffset: '-2px' },
      }}
    >
      <Checkbox
        checked={checked}
        size="small"
        tabIndex={-1}
        sx={{
          p: 0.5,
          '& .MuiSvgIcon-root': { fontSize: 17 },
          '&.Mui-checked': { color: accentColor },
        }}
      />
      <Typography
        sx={{
          ml: 0.25,
          fontSize: OPTION_FONT_SIZE,
          fontWeight: checked ? 600 : 400,
          color: checked ? theme.palette.v4.textMuted : theme.palette.text.primary,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export interface GroupHeaderProps {
  group: OptionGroup;
  selected: string[];
  onChange: (next: string[]) => void;
}

export const GroupHeader: React.FC<GroupHeaderProps> = ({ group, selected, onChange }) => {
  const theme = useTheme();
  const allSelected = group.values.every((v) => selected.includes(v));

  // As with the other in-popper buttons: Enter here must not bubble up to
  // FilterBar's Enter-to-search handler and fire a stale search.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') e.stopPropagation();
  };

  const handleToggleGroup = () => {
    if (allSelected) {
      onChange(selected.filter((s) => !group.values.includes(s)));
    } else {
      const merged = new Set([...selected, ...group.values]);
      onChange(Array.from(merged));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1,
        pt: 1.25,
        pb: 0.25,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: '10.5px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          color: theme.palette.v4.textFaint,
        }}
      >
        {group.name}
      </Typography>
      {/* "All" / "None" rather than another "Select all": the popper's own
          bulk action already carries that name, and two buttons with the same
          accessible name in one listbox is ambiguous to read and to test —
          the old pair was told apart only by its capital A. */}
      <Link
        component="button"
        variant="caption"
        onClick={handleToggleGroup}
        onKeyDown={handleKeyDown}
        aria-label={`${allSelected ? 'Clear' : 'Select all'} ${group.name}`}
        sx={{
          color: theme.palette.v4.accentDark,
          cursor: 'pointer',
          fontSize: '11px',
          fontWeight: 600,
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {allSelected ? 'None' : 'All'}
      </Link>
    </Box>
  );
};
